/* «Nytt arbeid» i LOS Marine Workspace som replika: det tomme feltet, forespørselen som slippes inn, assistentens funn
   (kunde · fartøy · arbeidet · kontakt · 28 arbeidsposter · 5 uklarheter) og «Opprett arbeid». `at` = hvor langt manuset er kommet. */
import { ArrowRight, Check, FileText, Package, ScanSearch, Sparkles } from "lucide-react";
import { LosMark } from "@/components/Logo";
import { Avatar, Primary, Shell, WorkPanel } from "./LosShell";
import { Composer } from "./LosViews";
import { CASE, FINDINGS, USER } from "./losMockData";

export const PROMPT = "Lag et tilbud basert på denne forespørselen";
const FILES = ["Yard_jobs_export_MS_Nordlys.pdf · 114 s.", "GA_MS_Nordlys_rev3.pdf", "Bilder_skrog_2026.zip"];

const Starters = () => (
  <div className="mt-5 flex flex-wrap justify-center gap-2 text-[13px]">
    {[["Opprett et tilbud", FileText], ["Analyser dokumenter", ScanSearch], ["Finn produkter og priser", Package]].map(([l, Icon]) => (
      <span key={l} className="inline-flex h-9 items-center gap-2 rounded-full border border-black/[0.08] bg-white px-3.5 text-foreground/80"><Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} /> {l}</span>
    ))}
  </div>
);

const Hero = ({ at, nar }) => (
  <div className={`mx-auto flex h-full max-w-[760px] flex-col justify-center ${nar ? "px-4" : "px-8"}`}>
    <div className="flex flex-col items-center text-center">
      <LosMark tone="bare" className="h-9 w-9" />
      <h1 className={`mt-6 font-sans font-medium leading-[1.15] tracking-[-0.022em] text-foreground ${nar ? "text-[26px]" : "text-[32px]"}`}>Hva vil du jobbe med, {USER.first}?</h1>
    </div>
    <div className="mt-7"><Composer nar={nar} value={at >= 0 ? PROMPT : ""} files={at >= 0 ? FILES : []} typing={at === 0} /></div>
    {at < 0 ? <Starters /> : <p className="mb-fade mt-4 text-center text-[12.5px] text-muted-foreground">3 filer lagt til · assistenten leser når du sender</p>}
  </div>
);

const Field = ({ k, v, basis }) => (
  <div className="min-w-0">
    <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{k}</div>
    <div className="mt-0.5 truncate border-b border-dashed border-black/[0.18] pb-0.5 text-[14px] font-medium text-foreground">{v}</div>
    <div className="mt-1 text-[11px] text-muted-foreground">{basis}</div>
  </div>
);

export const FindingsCard = ({ done, nar }) => (
  <div className="rounded-[16px] border border-black/[0.07] bg-white p-4 shadow-[0_1px_2px_hsl(var(--shadow-color)/0.05),0_16px_40px_-24px_hsl(var(--shadow-color)/0.3)] sm:p-5" data-testid="los-findings">
    <div className={`grid gap-x-6 gap-y-4 ${nar ? "grid-cols-1" : "grid-cols-2"}`}>{FINDINGS.fields.map(([k, v, b]) => <Field key={k} k={k} v={v} basis={b} />)}</div>
    <div className="mt-5 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-foreground/85">{FINDINGS.shape.map((s, i) => <span key={s} className="inline-flex items-center gap-2">{i ? <span className="h-1 w-1 rounded-full bg-black/[0.25]" /> : null}{i === 0 ? <b className="font-semibold text-foreground">{s}</b> : s}</span>)}</div>
    <div className="mt-3 flex flex-wrap gap-1.5">{FINDINGS.docs.map((d) => <span key={d} className="inline-flex h-7 items-center gap-1.5 rounded-full bg-[hsl(var(--sidebar))] px-2.5 text-[12px] text-foreground/85 ring-1 ring-black/[0.06]"><FileText className="h-3.5 w-3.5 text-muted-foreground" /> {d}</span>)}</div>
    <div className="mt-5">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--status-warn))]">Uklart i forespørselen · {FINDINGS.unclear.length}</div>
      <ul className="mt-1.5 space-y-1 text-[13px] leading-[1.5] text-foreground/85">{FINDINGS.unclear.slice(0, nar ? 3 : 5).map((u) => <li key={u} className="flex gap-2"><span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-[hsl(var(--status-warn))]" />{u}</li>)}</ul>
    </div>
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-black/[0.06] pt-4">
      <span className="text-[12.5px] text-muted-foreground">{done ? <span className="mb-fade inline-flex items-center gap-1.5 font-medium text-[hsl(var(--status-ok))]"><Check className="h-4 w-4" strokeWidth={2.4} /> Arbeidet er opprettet · #{CASE.number} · åpner saken</span> : "Ingenting er opprettet ennå. Rett det som er feil, så oppretter vi saken."}</span>
      <Primary icon={done ? Check : ArrowRight} className={done ? "bg-[hsl(var(--status-ok))]" : ""}>{done ? "Opprettet" : "Opprett arbeid"}</Primary>
    </div>
  </div>
);

const Chat = ({ at, nar }) => (
  <div className="flex h-full min-h-0 flex-col">
    <div className={`flex h-12 shrink-0 items-center justify-between border-b border-black/[0.05] ${nar ? "px-4" : "px-6"}`}>
      <div className="min-w-0 leading-tight"><div className="truncate text-[14.5px] font-semibold text-foreground">{at >= 2 ? CASE.name : "Nytt arbeid"}</div><div className="text-[11.5px] text-muted-foreground">{at >= 2 ? "Arbeidet er opprettet" : "Lagres automatisk"}</div></div>
    </div>
    <div className={`mb-noscroll min-h-0 flex-1 overflow-hidden ${nar ? "px-4 py-4" : "px-8 py-6"}`}>
      <div className="mx-auto max-w-[760px] space-y-5">
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-[18px] rounded-br-[6px] bg-[hsl(var(--sidebar))] px-4 py-3 text-[14px] leading-[1.5] text-foreground">
            {PROMPT}
            <div className="mt-2 flex flex-wrap gap-1.5">{FILES.map((f) => <span key={f} className="inline-flex h-7 items-center gap-1.5 rounded-full bg-white px-2.5 text-[12px] text-foreground/85 ring-1 ring-black/[0.06]"><FileText className="h-3.5 w-3.5 text-muted-foreground" />{f}</span>)}</div>
          </div>
        </div>
        <div className="mb-fade flex gap-3">
          <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--navy))] text-white"><Sparkles className="h-3.5 w-3.5" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] leading-[1.6] text-foreground/90">Jeg har lest de tre dokumentene. Forespørselen gjelder <b>5-årsklassing av {CASE.vessel}</b> for {CASE.customer}, med dokking i uke 12–14 2027. Jeg fant <b>28 arbeidsposter</b> med SFI- og WO-nummer og <b>5 punkter</b> som bør avklares før kalkylen. Sjekk det som står under – så oppretter vi saken.</p>
            <div className="mt-4"><FindingsCard done={at >= 2} nar={nar} /></div>
          </div>
        </div>
      </div>
    </div>
    <div className={`shrink-0 border-t border-black/[0.05] ${nar ? "px-4 py-3" : "px-8 py-4"}`}><div className="mx-auto flex h-11 max-w-[760px] items-center justify-between rounded-full border border-black/[0.08] bg-white px-4 text-[13.5px] text-muted-foreground">Spør om forespørselen, eller rett noe … <Avatar size={24} /></div></div>
  </div>
);

export const IntakeStill = ({ at = -1, mode = "full" }) => {
  const nar = mode === "narrow";
  return (
    <div className="h-full min-h-0" data-testid="los-mock-ny" data-at={at}>
      {at <= 0 ? <Hero at={at} nar={nar} /> : <Chat at={at} nar={nar} />}
    </div>
  );
};

export const IntakeApp = ({ at, mode, toast }) => (
  <Shell mode={mode} active="arbeid" panel={<WorkPanel activeName={at >= 2 ? CASE.name : "__none__"} />} title="Nytt arbeid" toast={toast} testId="los-intake-app" view="ny" beat={at}>
    <IntakeStill at={at} mode={mode} />
  </Shell>
);
